'use client'

import { useEffect } from 'react'
import { usePathname, useRouter } from '@/lib/next-shim'

import { ConsultationStatus, GetConsultationQuery } from '@/core/graphql/types'
import { useGetConsultationQuery, useGetMyInfosQuery } from '@/core/graphql/hooks'
import { useIsFranceMode } from '@/hooks/useIsFranceMode'
import { isPatientReady } from '@/utils/profile-readiness'
import { useRequiredParams } from '@/hooks/useRequiredParams'

/**
 * Discriminated state driving what the waiting page renders. Both queries must
 * settle before anything is shown so the waiting room never flashes.
 */
export type WaitingGateState =
  | { status: 'loading' }
  | { status: 'profileError'; retry: () => void }
  | { status: 'notReady' }
  | { status: 'consultationError' }
  | { status: 'notFound' }
  | { status: 'ready'; consultationId: string; consultation: NonNullable<GetConsultationQuery['consultation']> }

export function useConsultationWaitingGate(): WaitingGateState {
  const { consultationId } = useRequiredParams(['consultationId'])
  const router = useRouter()
  const pathname = usePathname()
  const isFrance = useIsFranceMode()

  const { data, loading, error } = useGetConsultationQuery({
    variables: { id: consultationId },
    skip: !consultationId,
  })

  // Bill-ready gate: the patient must have a complete profile (France: incl. NIR) BEFORE
  // entering the waiting room — once queued, a doctor can pick them up at any time and
  // billing can no longer be enforced.
  const { data: myInfosData, loading: myInfosLoading, error: myInfosError, refetch: refetchMyInfos } = useGetMyInfosQuery()
  const myInfos = myInfosData?.myInfos
  const ready = isPatientReady(myInfos, isFrance)

  const consultation = data?.consultation

  useEffect(() => {
    if (myInfosLoading || myInfosError) return
    if (myInfos && !ready) {
      const back = pathname || `/account/consultations/${consultationId}/waiting`
      router.replace(`/complete-profile?callbackUrl=${encodeURIComponent(back)}`)
    }
  }, [myInfos, ready, myInfosLoading, myInfosError, pathname, consultationId, router])

  useEffect(() => {
    if (!ready) return // never advance an unready patient into the live consultation
    if (!loading && !consultation && !error) {
      router.push('/account/consultations')
    }
    if (consultation?.status === ConsultationStatus.Active) {
      router.push(`/account/consultations/${consultationId}/live`)
    }
  }, [consultation, loading, error, consultationId, router, ready])

  // Wait for BOTH queries before deciding anything — never flash the waiting room.
  if (loading || myInfosLoading) return { status: 'loading' }

  // On profile-check failure, block + offer retry (billing safety — do not let an
  // unverified patient into the queue on a transient error).
  if (myInfosError) return { status: 'profileError', retry: () => refetchMyInfos() }

  // Not ready -> redirecting to complete-profile; show a neutral loader meanwhile.
  if (!ready) return { status: 'notReady' }

  if (error) {
    console.error('[WaitingPage] failed to load consultation:', error)
    return { status: 'consultationError' }
  }

  if (!consultation) return { status: 'notFound' }

  return { status: 'ready', consultationId, consultation }
}
